import { useCallback, useState } from 'react'
import { useContatos, type ContatoItem } from '@hooks/useContatos.js'
import type { ContatoPapel } from '@services/db.js'
import { PageShell } from '@components/layout/PageShell.js'
import { ModePageHeader } from '@components/layout/ModePageHeader.js'
import { NavIcon } from '@components/layout/NavIcons.js'
import { Button } from '@components/primitives/Button.js'
import { EmptyPanel } from '@components/primitives/EmptyPanel.js'
import { CardListSkeleton, PageHeaderSkeleton } from '@components/primitives/Skeleton.js'
import { ContatoCard } from '@components/contatos/ContatoCard.js'
import { ContatoFormModal } from '@components/contatos/ContatoFormModal.js'

export function ContatosPage() {
  const { itens, todos, filtros, setFiltros, papeisPresentes, isLoading, criar, editar, remover } =
    useContatos()
  const [modalOpen, setModalOpen] = useState(false)
  const [editando, setEditando] = useState<ContatoItem | null>(null)

  const abrirNovo = useCallback(() => {
    setEditando(null)
    setModalOpen(true)
  }, [])

  const abrirEditar = useCallback((item: ContatoItem) => {
    setEditando(item)
    setModalOpen(true)
  }, [])

  if (isLoading) {
    return (
      <PageShell>
        <PageHeaderSkeleton label='Carregando contatos…' />
        <section className='cf3-block'>
          <CardListSkeleton count={3} label='Carregando contatos…' />
        </section>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <ModePageHeader
        title='Contatos da obra'
        subtitle='Quem trabalha e quem fornece. Ligue ou mande WhatsApp em um toque.'
      >
        <Button variant='primary' size='sm' onClick={abrirNovo}>
          Novo contato
        </Button>
      </ModePageHeader>

      {todos.length === 0 ? (
        <EmptyPanel
          icon={<NavIcon icon='contatos' size={24} />}
          title='Nenhum contato ainda'
          description='Cadastre pedreiro, empreiteiro, eletricista, engenheiro e fornecedores. Os contatos aparecem nos contratos e no diário (quem trabalhou no dia).'
          actionLabel='Cadastrar contato'
          onAction={abrirNovo}
        />
      ) : (
        <>
          <section className='cf3-block'>
            <input
              type='search'
              className='cf-input'
              placeholder='Buscar por nome, empresa ou função…'
              value={filtros.busca}
              onChange={(e) => setFiltros((f) => ({ ...f, busca: e.target.value }))}
              aria-label='Buscar contatos'
            />
            {papeisPresentes.length > 1 && (
              <div className='cf3-chip-row' role='group' aria-label='Filtrar por função'>
                <button
                  type='button'
                  className={`cf3-chip${filtros.papel === '' ? ' cf3-chip--active' : ''}`}
                  aria-pressed={filtros.papel === ''}
                  onClick={() => setFiltros((f) => ({ ...f, papel: '' }))}
                >
                  Todos
                </button>
                {papeisPresentes.map((p) => (
                  <button
                    key={p.value}
                    type='button'
                    className={`cf3-chip${filtros.papel === p.value ? ' cf3-chip--active' : ''}`}
                    aria-pressed={filtros.papel === p.value}
                    onClick={() => setFiltros((f) => ({ ...f, papel: p.value as ContatoPapel }))}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className='cf3-block' aria-label='Lista de contatos'>
            {itens.length === 0 ? (
              <EmptyPanel
                title='Nenhum resultado'
                description='Nenhum contato corresponde à busca ou ao filtro.'
                actionLabel='Limpar filtros'
                onAction={() => setFiltros({ busca: '', papel: '' })}
              />
            ) : (
              <>
                <p className='cf3-pend-lista__count' aria-live='polite'>
                  {itens.length === todos.length
                    ? `${todos.length} contato${todos.length !== 1 ? 's' : ''}`
                    : `${itens.length} de ${todos.length} contatos`}
                </p>
                <div className='cf3-gestao-list'>
                  {itens.map((c) => (
                    <ContatoCard key={c.id} item={c} onEditar={abrirEditar} onRemover={remover} />
                  ))}
                </div>
              </>
            )}
          </section>
        </>
      )}

      <ContatoFormModal
        open={modalOpen}
        initial={editando}
        onClose={() => {
          setModalOpen(false)
          setEditando(null)
        }}
        onSalvar={criar}
        onEditar={editar}
      />
    </PageShell>
  )
}
